import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { lineMask, easeOut } from "../../lib/motion";

// Sunny Pacific Northwest inlet — evergreens, bright blue water, warm light
const HERO_BG = "/hero-pnw.jpg";

const TAGLINE_LINES = ["Adaptive software", "& technical consulting"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.12]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex min-h-screen items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Sunny coastal background — kept vivid, no heavy wash */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0" aria-hidden>
        <img
          src={HERO_BG}
          alt=""
          className="h-full w-full object-cover"
          style={{ filter: "saturate(1.08) brightness(1.04)" }}
        />
        {/* Faint left wash only — the image itself carries clean negative space for the text */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/35 via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#F6F9FC]" />
      </motion.div>

      {/* Full-width brand banner strip — sits below the fixed navbar */}
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: easeOut, delay: 0.3 }}
        className="absolute inset-x-0 top-[72px] z-20 border-b border-[#0C2340]/10 bg-white/95 backdrop-blur-sm md:top-[76px]"
        data-testid="hero-brand-banner"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-center gap-4 px-6 py-3 md:py-4">
          <img
            src="/logo.png"
            alt="Tide Water Studios emblem"
            className="h-24 w-24 rounded-full object-cover shadow-sm md:h-28 md:w-28"
            data-testid="hero-logo"
          />
          <span
            className="font-display text-xl tracking-wide text-[#0C2340] md:text-2xl"
            data-testid="hero-overline"
          >
            Tide Water Studios <span className="text-[#47586E]">Inc.</span>
          </span>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-20 mx-auto w-full max-w-[1400px] px-6 pb-24 pt-64 md:px-10 md:pb-20 md:pt-72"
      >
        <div className="max-w-2xl">
          <h1
            className="font-display text-[2.6rem] leading-[1.03] tracking-tight sm:text-5xl lg:text-6xl xl:text-7xl"
            data-testid="hero-title"
          >
            {TAGLINE_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.14em]">
                <motion.span
                  custom={i}
                  variants={lineMask}
                  initial="hidden"
                  animate="visible"
                  className="block text-[#0C2340]"
                  style={{ textShadow: "0 1px 2px rgba(255,255,255,0.7)" }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: easeOut, delay: 0.4 }}
            className="mt-8 max-w-xl font-body text-base font-medium leading-relaxed text-[#12324F] md:text-lg"
            style={{ textShadow: "0 1px 2px rgba(255,255,255,0.6)" }}
            data-testid="hero-subtitle"
          >
            High-precision tools for businesses operating in complex, tactical,
            and Northern environments — built in Whitehorse, Yukon.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: easeOut, delay: 0.55 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#fleet"
              className="btn-ink font-mono-2 rounded-sm px-7 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em]"
              data-testid="hero-explore-button"
            >
              See Our Products
            </a>
            <a
              href="#contact"
              className="font-mono-2 rounded-sm border border-[#0C2340]/40 bg-white/60 px-7 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-[#0C2340] backdrop-blur-sm transition-colors duration-400 hover:border-[#0C2340] hover:bg-white/85"
              data-testid="hero-contact-button"
            >
              Start a Conversation
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 z-20 hidden -translate-x-1/2 md:block"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="overline overline-dark text-[0.6rem]">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="h-10 w-px bg-gradient-to-b from-[#0C2340]/50 to-transparent"
          />
        </div>
      </motion.div>
    </section>
  );
};
